import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import ActiveStorageUpload from "../uppy-activestorage-upload"

export default class extends Controller {
  static values = {
    uploadUrl: String,
    uploadParam: { type: String, default: "photo_question[photo]" },
    uploadParams: { type: String, default: "{}" },
    targetSelector: { type: String, default: "" },
    allowedFileTypes: { type: Array, default: ["image/*"] }
  }

  connect() {
    const directUploadUrl = document.querySelector("meta[name='direct-upload-url']")?.getAttribute("content")
    
    if (!directUploadUrl) {
      console.error("UppyUpload: direct-upload-url meta tag not found")
      return
    }

    if (!this.uploadUrlValue) {
      console.error("UppyUpload: uploadUrl data attribute is required")
      return
    }

    // Parse uploadParams JSON with error handling
    let parsedUploadParams = {}
    try {
      parsedUploadParams = JSON.parse(this.uploadParamsValue || "{}")
    } catch (e) {
      console.error("UppyUpload: Failed to parse uploadParams JSON:", e, "Raw value:", this.uploadParamsValue)
      parsedUploadParams = {}
    }
    this.parsedUploadParams = parsedUploadParams

    this.uppy = new Uppy({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: this.allowedFileTypesValue
      }
    })

    this.uppy.use(ActiveStorageUpload, {
      directUploadUrl: directUploadUrl
    })

    // Handle successful uploads
    this.uppy.on("upload-success", (file, blob) => {
      // Extract signed_id from blob or file response
      const signedId = blob?.signed_id || file?.response?.directUploadSignedId
      if (signedId) {
        this.submitToRails(file, { signed_id: signedId })
      } else {
        console.error("No signed_id found in upload response")
        this.showError(file, new Error("Upload completed but no signed_id received"))
      }
    })

    // Handle upload errors
    this.uppy.on("upload-error", (file, error) => {
      console.error("Upload error:", error)
      this.showError(file, error)
    })

    // Handle file removal
    this.uppy.on("file-removed", (file) => {
      // Clean up any UI elements if needed
    })

    // Enable drag and drop on the element
    this.setupDragAndDrop()
  }

  disconnect() {
    if (this.uppy) {
    //   this.uppy.close()
    }
    // Clean up file input if it exists
    if (this.fileInput && this.fileInput.parentNode) {
      this.fileInput.parentNode.removeChild(this.fileInput)
    }
  }

  setupDragAndDrop() {
    const element = this.element
    const preventDefaults = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }

    // Prevent default drag behaviors
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      element.addEventListener(eventName, preventDefaults, false)
    })

    // Highlight drop area when item is dragged over it
    ;["dragenter", "dragover"].forEach(eventName => {
      element.addEventListener(eventName, () => {
        element.classList.add("drag-over")
      }, false)
    })

    ;["dragleave", "drop"].forEach(eventName => {
      element.addEventListener(eventName, () => {
        element.classList.remove("drag-over")
      }, false)
    })

    // Handle dropped files
    element.addEventListener("drop", (e) => {
      const files = Array.from(e.dataTransfer.files)
      files.forEach(file => {
        this.uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          source: "Local"
        })
      })
    }, false)

    // Handle click to select files - only on drop zone areas
    this.setupClickToUpload()
  }

  setupClickToUpload() {
    const element = this.element
    
    // Find all drop zone areas (elements with data-uppy-drop-zone attribute)
    const dropZones = element.querySelectorAll("[data-uppy-drop-zone]")
    
    // Create a single hidden file input that we'll reuse
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.multiple = true
    fileInput.accept = this.allowedFileTypesValue.join(",")
    fileInput.style.display = "none"
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        this.uppy.addFile({
          name: file.name,
          type: file.type,
          data: file,
          source: "Local"
        })
      })
      // Reset the input so the same file can be selected again
      event.target.value = ""
    }
    // Append to body to ensure it's in the DOM
    document.body.appendChild(fileInput)

    // Add click handlers to drop zone areas
    dropZones.forEach(dropZone => {
      dropZone.addEventListener("click", (e) => {
        // Don't trigger if clicking on a link or button inside the drop zone
        if (!e.target.closest("a") && !e.target.closest("button")) {
          // Trigger the file picker synchronously within the user interaction
          fileInput.click()
          e.preventDefault()
          e.stopPropagation()
        }
      })
    })

    // Clean up on disconnect
    this.fileInput = fileInput
  }

  async submitToRails(file, blob) {
    const formData = new FormData()
    
    // Add the signed_id
    formData.append(this.uploadParamValue, blob.signed_id)

    // Add additional params (use parsed version)
    Object.entries(this.parsedUploadParams || {}).forEach(([key, value]) => {
      formData.append(key, value)
    })

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.getAttribute("content"),
          "Accept": "application/json"
        },
        body: formData
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: "Upload failed" }))
        throw new Error(error.error || "Upload failed")
      }

      const data = await response.json()
      this.handleSuccess(file, data)
    } catch (error) {
      console.error("Error submitting to Rails:", error)
      this.showError(file, error)
    }
  }

  handleSuccess(file, data) {
    // Dispatch a custom event for other controllers to listen to
    this.dispatch("upload-success", {
      detail: { file, data },
      bubbles: true
    })

    // Always reload the page after successful upload to show the new photo
    // This works whether it's the first photo or subsequent ones
    if (data.files && data.files.length > 0) {
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }

  showError(file, error) {
    // Dispatch error event
    this.dispatch("upload-error", {
      detail: { file, error },
      bubbles: true
    })

    // Show user-friendly error message
    console.error(`Upload failed for ${file.name}:`, error)
  }
}

